import * as z from "zod";
import {
  configurableAIProviderIds,
  effortLevels,
  getAIProvider,
} from "../aiProviders";
import { isFeatureFlagEnabled } from "../featureFlags";
import { userFacingError } from "../authorization";
import {
  dropConnectorUsage,
  recordConnectorUsage,
  usageRef,
} from "../connectorUsages";
import { zql } from "../generatedDrizzleSchema";
import { assertAccess } from "../rbac/access";
import { mutator } from "./mutator";

export const updateAIConfigSchema = z.object({
  id: z.string(),
  active: z.boolean(),
  provider: z.enum(configurableAIProviderIds),
  baseUrl: z.string(),
  caCertificate: z.string().optional(),
  chatModel: z.string(),
  fastModel: z.string(),
  effort: z.enum(effortLevels).nullable().optional(),
  connectedAppId: z.string().nullable().optional(),
  inheritConnectorAccess: z.boolean().optional(),
});

export const updateAIConfig = mutator({
  description:
    "Configures an AI provider’s models, default status, and access.",
  interactionType: "update",
  schema: updateAIConfigSchema,
  resolve: {},
  permissions: [
    {
      permission: "tenant:ai_providers_manage",
      scopeId: ({ ctx }) => ctx.tenantId,
    },
  ],
  fn: async ({ tx, ctx, args }) => {
    const now = Date.now();
    // Scope to the caller's tenant: the lookup is otherwise unscoped (no RLS),
    // so an unscoped read would let the deactivation loop below flip every other
    // tenant's active provider and let args.id name a provider in another tenant.
    const existingConfigs = await tx.run(
      zql.aiProviders.where("tenantId", ctx.tenantId)
    );
    const existing = existingConfigs.find((config) => config.id === args.id);

    const definition = getAIProvider(args.provider);
    if (
      existing?.provider !== args.provider &&
      definition.featureFlag &&
      !(await isFeatureFlagEnabled(tx, definition.featureFlag, ctx.tenantId))
    ) {
      throw userFacingError("This AI provider is not available");
    }

    // Deactivating the default hands it to the managed provider, so it's only
    // allowed while that provider is enabled.
    if (existing?.active && !args.active) {
      const [tenant] = await tx.run(zql.tenants.where("id", ctx.tenantId));
      if (
        tenant?.managedAiProviderLocked ||
        tenant?.managedAiProviderDisabledAt != null
      ) {
        throw userFacingError(
          "The managed AI provider is disabled and can't become the default"
        );
      }
    }

    if (args.active && existing?.disabledAt != null) {
      throw userFacingError(
        "Enable this provider before making it the default"
      );
    }

    const ref = usageRef.aiProvider(args.id);
    const [existingUsage] = await tx.run(
      zql.connectedAppUsages.where("type", ref.type).where("refId", ref.refId)
    );
    const currentAppId = existingUsage?.connectedAppId ?? null;
    const inheritConnectorAccess =
      args.inheritConnectorAccess ?? existing?.inheritConnectorAccess ?? false;
    const connectedAppId =
      args.connectedAppId === undefined ? currentAppId : args.connectedAppId;
    if (inheritConnectorAccess && !connectedAppId) {
      throw userFacingError("Choose a connector before using connector access");
    }
    if (inheritConnectorAccess && args.active) {
      throw userFacingError(
        "The default AI provider must be available to everyone in the tenant"
      );
    }

    // Re-check connected_app:use only when the binding changes or the restriction
    // is lifted — lifting it hands the whole tenant use of the connector, so
    // editing models/effort/active alone doesn't require rights on the app.
    const rebinding =
      args.connectedAppId !== undefined && args.connectedAppId !== currentAppId;
    const liftingRestriction =
      existing?.inheritConnectorAccess === true && !inheritConnectorAccess;
    if (connectedAppId && (rebinding || liftingRestriction)) {
      // principal-exempt: reachable only via the browser /zero/mutate path and
      // the session-only updateAIConfig server mutator; ctx.principal is never a
      // service account here.
      await assertAccess({
        tx,
        userId: ctx.userId,
        scopeId: connectedAppId,
        tenantId: ctx.tenantId,
        permission: "connected_app:use",
      });
    }

    const values = {
      active: args.active,
      inheritConnectorAccess,
      provider: args.provider,
      baseUrl: args.baseUrl || null,
      chatModel: args.chatModel || null,
      fastModel: args.fastModel || null,
      updatedAt: now,
    };

    for (const config of existingConfigs) {
      if (!config.active || config.id === args.id || !args.active) continue;
      await tx.mutate.aiProviders.update({
        id: config.id,
        active: false,
        updatedAt: now,
      });
    }

    if (existing) {
      await tx.mutate.aiProviders.update({
        id: existing.id,
        ...values,
        // Preserve the configured effort when a caller (e.g. the composer model
        // picker) omits it; only overwrite when explicitly provided.
        effort:
          args.effort !== undefined ? args.effort : (existing.effort ?? null),
      });
    } else {
      await tx.mutate.aiProviders.insert({
        id: args.id,
        ...values,
        effort: args.effort ?? null,
        disabledAt: null,
        createdAt: now,
        tenantId: ctx.tenantId,
      });
    }

    if (rebinding) {
      if (currentAppId) {
        await dropConnectorUsage(tx, currentAppId, ref);
      }
      if (args.connectedAppId) {
        await recordConnectorUsage(tx, args.connectedAppId, ref, ctx.tenantId);
      }
    }
  },
});
